import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, AlertTriangle, Crown } from "lucide-react";
import { api } from "@/lib/api";
import { Loader } from "@/components/common";

export default function Verify() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => { api.get(`/drops/${id}/verify`).then(({ data }) => setData(data)).catch(() => setData(false)); }, [id]);

  if (data === null) return <Loader />;
  if (data === false) return <div className="text-center py-24 text-slate-400">No draw proof available yet.</div>;

  const p = data.proof;
  const verified = p.method === "random.org-signed";

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
      <Link to={`/drops/${id}`} className="text-slate-400 text-sm hover:text-gold">← Back to drop</Link>
      <h1 className="font-display text-3xl font-black text-white mt-3">Provably-Fair Verification</h1>
      <p className="text-slate-400 mt-1">{data.title}</p>

      <div className={`mt-6 rounded-2xl p-5 border flex items-start gap-3 ${verified ? "border-emerald-400/40 bg-emerald-400/10" : "border-amber-400/40 bg-amber-400/10"}`} data-testid="verify-status">
        {verified ? <ShieldCheck className="w-6 h-6 text-emerald-400 shrink-0" /> : <AlertTriangle className="w-6 h-6 text-amber-400 shrink-0" />}
        <div>
          <p className="font-bold text-white">{verified ? "Verified via RANDOM.ORG signed API" : "Local secure draw (fallback)"}</p>
          <p className="text-sm text-slate-300 mt-1">{verified ? "This draw used RANDOM.ORG's cryptographically-signed random numbers. The signed result below can be independently verified at random.org/signatures." : (p.note || "RANDOM.ORG key not configured.")}</p>
        </div>
      </div>

      <div className="mt-6">
        <h2 className="font-display text-lg font-bold text-white flex items-center gap-2"><Crown className="w-5 h-5 text-gold" /> Winners</h2>
        <div className="mt-3 space-y-2">
          {data.winners.map((w, i) => (
            <div key={i} className="card-surface rounded-xl p-3 flex items-center justify-between">
              <span className="text-white font-semibold">{w.name}</span>
              {w.seat && <span className="font-mono text-gold text-sm">Seat #{w.seat}</span>}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6">
        <h2 className="font-display text-lg font-bold text-white">Cryptographic proof</h2>
        <pre className="mt-3 card-surface rounded-xl p-4 text-xs font-mono text-slate-300 overflow-x-auto custom-scrollbar" data-testid="verify-proof">{JSON.stringify(p, null, 2)}</pre>
      </div>
    </div>
  );
}
